import { MemberUtils, type Season } from "@shared/member.utils";
import { useMemo } from "react";
import type { Course } from "../../../stores/course.store";
import type { Member } from "../../../stores/user.store";
import type { TemporarySubscription } from "../../../types/course";

export type CompanionWithStatus = Member & {
  hasSeasonSubscription: boolean;
};

export const useAvailableCompanions = (
  members: Member[] | undefined,
  currentMemberId: string | undefined,
  currentCourse: Course | null,
  currentSeason: Season | null,
  tempSubscriptions: Map<string, TemporarySubscription>
): CompanionWithStatus[] => {
  return useMemo(() => {
    if (!members || !currentCourse || !currentSeason || !currentMemberId) {
      return [];
    }

    return members
      .filter((member) => {
        // Must be adult
        const memberType = MemberUtils.getMemberType(
          member.date_of_birth,
          currentSeason
        );
        if (memberType !== "adult") {
          return false;
        }

        // Cannot be the subscriber themselves
        if (member.id === currentMemberId) {
          return false;
        }

        // Cannot be a companion elsewhere in DB
        const isCompanionInDb = members.some((m) =>
          m.id !== currentMemberId &&
          m.course_subscriptions?.some(
            (sub) =>
              sub.course_id === currentCourse.id &&
              sub.companion_id === member.id
          )
        );
        if (isCompanionInDb) {
          return false;
        }

        // Cannot be a companion elsewhere in temp subscriptions
        const isCompanionInTemp = Array.from(tempSubscriptions.values()).some(
          (sub) =>
            sub.subscriber_id !== currentMemberId &&
            sub.companion_id === member.id
        );
        if (isCompanionInTemp) {
          return false;
        }

        return true;
      })
      .map((member) => {
        // Check if companion has a season subscription
        const hasSeasonSubscription = member.subscriptions?.some(
          (sub) => sub.season_name === currentSeason.name
        );
        return {
          ...member,
          hasSeasonSubscription: hasSeasonSubscription || false,
        };
      });
  }, [
    members,
    currentCourse,
    currentSeason,
    currentMemberId,
    tempSubscriptions,
  ]);
};
